import React from 'react';
import { Paper, Box, Typography, Skeleton, Grid, Card, CardContent } from '@mui/material';
import { useIsMobile } from 'hooks/useIsMobile';

/**
 * @component StatsAtGlance
 * @description Displays 3-4 stat cards in a row with optional title and subtitle
 * @param {Object} props
 * @param {Array<{label: string, value: string|number, variant?: string}>} props.stats - Array of stats to display
 * @param {boolean} [props.loading=false] - Whether stats are loading
 * @param {string} [props.title] - Optional title to display above the stats
 * @param {string} [props.subtitle] - Optional subtitle to display below the title
 * @param {React.ReactNode} [props.children] - Optional children to render below the stats
 * @returns {JSX.Element}
 */
export function StatsAtGlance({ stats, loading = false, title, subtitle, children }) {
  const isMobile = useIsMobile();
  
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  return (
    <Paper sx={{ p: 2 }}>
      {title && (
        <Box sx={{ mb: subtitle ? 1 : 2 }}>
          <Typography 
            variant={isMobile ? "h6" : "h4"} 
            fontWeight="600" 
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ my: 2}}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {isMobile ? (
        <Grid container spacing={2}>
          {stats.map((stat, index) => {
            const valueVariant = stat.variant || (isMobile ? 'h6' : 'h4');
            
            return (
              <Grid item xs={6} key={index}>
                <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 2 }}>
                    {loading ? (
                      <Skeleton variant="text" width={60} height={isMobile ? 32 : 40} sx={{ mx: 'auto', mb: 1 }} />
                    ) : (
                      <Typography 
                        variant={valueVariant} 
                        fontWeight="bold" 
                        color="primary"
                        sx={{ mb: 1 }}
                      >
                        {formatNumber(stat.value)}
                      </Typography>
                    )}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {stats.map((stat, index) => {
            const valueVariant = stat.variant || 'h4';
            
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 2 }}>
                    {loading ? (
                      <Skeleton variant="text" width={60} height={40} sx={{ mx: 'auto', mb: 1 }} />
                    ) : (
                      <Typography 
                        variant={valueVariant} 
                        fontWeight="bold" 
                        color="primary"
                        sx={{ mb: 1 }}
                      >
                        {formatNumber(stat.value)}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      {children && (
        <Box sx={{ mt: 3 }}>
          {children}
        </Box>
      )}
    </Paper>
  );
}

